import { Component, ElementRef, HostListener } from '@angular/core';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ChartSelectionTypeModel,
  DashboardStatisticsModel,
} from '../../../../core/models/dashboard/dashboard-statistics.model';
import { DashboardHeaderItemsModel } from '../../../../core/models/dashboard/dashboard-headeritems.model';
import { CommonModule } from '@angular/common';
import { Chart, registerables } from 'chart.js';
import { FormsModule } from '@angular/forms';
import { translateChartLabels } from '../../../../utils/chart-labels-utils';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { UserRole } from '../../../../../enums/roles.enum';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { UserSettingsModel } from '../../../../core/models/user/user-settings.model';
import { UsersService } from '../../../../core/services/user/users.service';
import { ToastService } from '../../../../core/services/toast.service';

@Component({
  selector: 'app-dashboard-main',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, PopoverModule],
  templateUrl: './dashboard-main.component.html',
  styleUrl: './dashboard-main.component.scss',
})
export class DashboardMainComponent {
  private chart: Chart | undefined;
  dashboardStatistics: DashboardStatisticsModel = {
    totalPatientCount: 0,
    totalImageCount: 0,
    totalConsultationCount: 0,
    totalIncomeCount: 0,
    totalFollowUpPatientCountByTwelveMonth: 0,
    totalFollowUpPatientCountBySixMonth: 0,
    totalFollowUpPatientCountByThreeMonth: 0,
    totalPatientIncreasePercentageLastMonth: 0,
    totalImageIncreasePercentageLastMonth: 0,
    totalConsultationIncreasePercentageLastMonth: 0,
    totalIncomeIncreasePercentageLastMonth: 0,
    dailyAppointmentCaseCount: 0,
    totalAppointmentCaseIncreasePercentageLastMonth: 0,
    statistics: [],
  };

  dashboardHeaderItems: DashboardHeaderItemsModel[] = [
    {
      countKey: 'totalPatientCount',
      icon: '/assets/media/dashboard/users.svg',
      label: 'totalPatients',
      increasePercentageLastMonth: 0,
      duration: 'thisMonth',
    },
    {
      countKey: 'totalImageCount',
      icon: '/assets/media/dashboard/images.svg',
      label: 'totalImages',
      increasePercentageLastMonth: 0,
      duration: 'thisMonth',
    },
    {
      countKey: 'totalConsultationCount',
      icon: '/assets/media/dashboard/consultation.svg',
      label: 'totalConsultations',
      increasePercentageLastMonth: 0,
      duration: 'thisMonth',
    },
    {
      countKey: 'totalIncomeCount',
      icon: '/assets/media/dashboard/income.svg',
      label: 'totalIncome',
      increasePercentageLastMonth: 0,
      duration: 'thisMonth',
    },
  ];
  chartSelectionType: ChartSelectionTypeModel[] = [
    {
      value: 1,
      label: 'dashboard.selectbox.weekly',
    },
    {
      value: 2,
      label: 'dashboard.selectbox.monthly',
    },
    {
      value: 3,
      label: 'dashboard.selectbox.yearly',
    },
  ];
  totalPatientCount: 0;
  totalImageCount: 0;
  totalECGCount: 0;
  totalConsultationCount: 0;
  totalChartPatients: number[] = [];
  totalChartConsultations: number[] = [];
  totalChartIncome: number[] = [];
  chartLabels: string[] = [];
  selectedChartType: number = 1;
  private initialCanvasHeight: number | undefined;
  private initialCanvasWidth: number | undefined;
  daysOfWeek: string[] = [];
  years: number[] = [];
  currentUser: UserSessionModel | null = null;
  userRole: any = UserRole;
  priceValue: number = 0;
  invalidPriceValue: boolean = false;

  constructor(
    private patientsService: PatientsService,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private currentUserService: CurrentUserService,
    private userService: UsersService,
    private toastService: ToastService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.adjustCanvasHeight();
  }
  @HostListener('window:keydown.enter')
  onEnterKey(): void {
    this.updateUserPrice();
  }
  ngAfterViewInit(): void {
    this.adjustCanvasHeight();
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.priceValue = this.currentUser.price ?? 0;
    this.getDashboardInformations();
  }

  validatePrice(event: any) {
    const inputValue = event.target.value;
    const regex = /^(0|[1-9]\d?)([.,]\d{1,2})?$/;
    this.invalidPriceValue = !regex.test(inputValue);
  }

  updateUserPrice() {
    if (this.priceValue <= 0) {
      this.invalidPriceValue = true;
      return;
    }

    this.invalidPriceValue = false;
    const userData = {
      userId: this.currentUser.id,
      price: this.priceValue,
    } as UserSettingsModel;

    this.userService.updateUserPrice(userData).subscribe({
      next: (res) => {
        if (res) {
          this.updateCurrentUserSession(res);

          this.toastService.showToast(
            this.translate.instant('dashboard.userPriceUpdated'),
            'success'
          );
        } else {
          this.toastService.showToast(
            this.translate.instant('dashboard.errorUserPriceUpdate'),
            'error'
          );
        }
      },
      error: () => {
        this.toastService.showToast(
          this.translate.instant('dashboard.errorUserPriceUpdate'),
          'error'
        );
      },
    });
  }

  updateCurrentUserSession(res: any) {
    let currentUser = this.currentUserService.GetSession();
    currentUser.price = res.token.price;

    if (res && res?.token?.token) {
      currentUser.token = res.token.token;
    }

    this.currentUserService.SetSession(currentUser);
  }

  private adjustCanvasHeight(): void {
    const statisticsSection =
      this.elementRef.nativeElement.querySelector('.col-md-4');
    const canvas = this.elementRef.nativeElement.querySelector('#lineChart');
    if (statisticsSection && canvas) {
      const statisticsHeight = statisticsSection.offsetHeight;
      canvas.style.height = `${statisticsHeight}px`;
    }
  }

  private createChart(): void {
    const ctx = document.getElementById('lineChart') as HTMLCanvasElement;
    if (ctx) {
      const parentElement = ctx.parentElement;
      if (parentElement) {
        if (!this.initialCanvasHeight) {
          this.initialCanvasHeight = parentElement.clientHeight * 0.75;
        }
        if (!this.initialCanvasWidth) {
          this.initialCanvasWidth = parentElement.clientWidth;
        }
        ctx.height = this.initialCanvasHeight;
        ctx.width = this.initialCanvasWidth;
      }
    }

    ctx.style.height = `${this.initialCanvasHeight}px`;
    ctx.style.width = `${this.initialCanvasWidth}px`;
    ctx.style.maxHeight = '700px';

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.chartLabels,
        datasets: [
          {
            label: this.translate.instant('dashboard.patients'),
            data: this.totalChartPatients,
            fill: false,
            borderColor: '#0f123f',
            tension: 0.35,
          },
          {
            label: this.translate.instant('dashboard.consultations'),
            data: this.totalChartConsultations,
            fill: false,
            borderColor: '#fe981e',
            tension: 0.35,
          },
          {
            label: this.translate.instant('dashboard.totalIncome'),
            data: this.totalChartIncome,
            pointRadius: 0,
            borderWidth: 0,
            hoverBorderWidth: 0,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0)',
          },
        ],
      },
      options: {
        // maintainAspectRatio: false,
        responsive: true,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          y: {
            min: 0,
            beginAtZero: true,
            grid: {
              color: '#E2E2E2',
            },
          },
          x: {
            grid: {
              color: '#E2E2E2',
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              filter: (item) =>
                item.text !== this.translate.instant('dashboard.totalIncome'),
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (
                  context.dataset.label ===
                  this.translate.instant('dashboard.totalIncome')
                ) {
                  if (this.currentUser?.role === UserRole.Dermatologist) {
                    label += '€' + context.parsed.y;
                  } else {
                    return null;
                  }
                } else {
                  label += context.parsed.y;
                }
                return label;
              },
            },
          },
        },
      },
    });
  }

  onChangeChart(e: Event): void {
    if (this.chart) {
      const ctx = document.getElementById('lineChart') as HTMLCanvasElement;
      if (ctx) {
        this.initialCanvasHeight = ctx.height;
        this.initialCanvasWidth = ctx.width;
      }
      this.chart.destroy();
    }
    this.getDashboardInformations();
  }

  public updateChartData(type: number): void {
    if (this.chart) {
      if (type == 1) {
        this.chart.data.labels = this.daysOfWeek;
      } else if (type == 2) {
        this.chart.data.labels = Array.from({ length: 30 }, (_, i) =>
          (i + 1).toString()
        );
      } else if (type == 3) {
        this.chart.data.labels = this.years;
      }
      this.chart.update();
    }
  }

  getDashboardInformations(): void {
    this.patientsService.dashboardStatistics(this.selectedChartType).subscribe({
      next: (res) => {
        this.dashboardStatistics = res;
        const stats = res?.statistics || [];

        this.totalChartPatients = stats.map((item) => item.totalPatients);
        this.totalChartConsultations = stats.map(
          (item) => item.totalConsultations
        );
        this.totalChartIncome = stats.map((item) => item.totalIncome);
        this.daysOfWeek = stats.map((item) =>
          translateChartLabels(this.translate, item.dayOfWeek, 'day')
        );
        this.years = stats.map((item) =>
          translateChartLabels(this.translate, item.month, 'year')
        );
        this.dashboardHeaderItems[0].increasePercentageLastMonth =
          this.dashboardStatistics.totalPatientIncreasePercentageLastMonth;
        this.dashboardHeaderItems[1].increasePercentageLastMonth =
          this.dashboardStatistics.totalImageIncreasePercentageLastMonth;
        this.dashboardHeaderItems[2].increasePercentageLastMonth =
          this.dashboardStatistics.totalConsultationIncreasePercentageLastMonth;
        this.dashboardHeaderItems[3].increasePercentageLastMonth =
          this.dashboardStatistics.totalIncomeIncreasePercentageLastMonth;

        if (
          this.currentUser?.role === this.userRole.GeneralPractitioner ||
          this.currentUser?.role === this.userRole.Admin ||
          this.currentUser?.role === this.userRole.OrganizationAdmin
        ) {
          this.dashboardHeaderItems.pop();
          this.dashboardHeaderItems.push({
            countKey: 'dailyAppointmentCaseCount',
            icon: '/assets/media/dashboard/appointment.svg',
            label: 'appointmentsToday',
            increasePercentageLastMonth:
              this.dashboardStatistics
                .totalAppointmentCaseIncreasePercentageLastMonth,
            duration: 'thisMonth',
          });
        }

        Chart.register(...registerables);
        this.createChart();
        this.updateChartData(this.selectedChartType);
        const appointmentTodayCart = (x) => {};
      },
    });
  }

  getArrowStyle(percentage: number): string {
    if (percentage > 0) {
      return 'text-success fa-arrow-up rotate-45';
    } else if (percentage < 0) {
      return 'text-danger fa-arrow-down rotate-45';
    } else {
      return 'fa-minus';
    }
  }
}
